/**
 * WorkWise is exclusive in the product picker: its servers are configured in
 * the standard wizard steps and it cannot be combined with other products. Selecting it
 * clears the others; selecting another product clears it.
 *
 * `enforceWorkwiseExclusive` post-processes whatever the store's original toggle
 * logic produced, so selections that never involve WorkWise are returned
 * untouched (non-WorkWise behaviour is unchanged).
 */
import { WORKWISE_PRODUCT_SLUG } from '../../config/workwise'
import type { Product, ProductSize } from '../../types'

export interface ProductSelection {
  product: Product
  size: ProductSize
}

export type SelectionNotice = 'others-cleared' | 'workwise-cleared'

export function isWorkwiseProduct(product: Pick<Product, 'slug'>): boolean {
  return product.slug === WORKWISE_PRODUCT_SLUG
}

export function hasWorkwise(selection: ProductSelection[]): boolean {
  return selection.some((s) => isWorkwiseProduct(s.product))
}

export function enforceWorkwiseExclusive(
  previous: ProductSelection[],
  next: ProductSelection[],
): { selection: ProductSelection[]; notice: SelectionNotice | null } {
  const workwise = next.filter((s) => isWorkwiseProduct(s.product))
  const others = next.filter((s) => !isWorkwiseProduct(s.product))
  if (workwise.length === 0 || others.length === 0) {
    return { selection: next, notice: null }
  }
  const workwiseWasSelected = hasWorkwise(previous)
  if (workwiseWasSelected) {
    // WorkWise was already chosen and something else was just added: drop WorkWise.
    return { selection: others, notice: 'workwise-cleared' }
  }
  // WorkWise was just added next to other products: keep WorkWise only.
  return { selection: workwise.slice(0, 1), notice: 'others-cleared' }
}

export function selectionNoticeMessage(notice: SelectionNotice | null, workwiseSelected: boolean): string | null {
  if (notice === 'others-cleared') {
    return 'WorkWise is deployed on its own. The other selected products were cleared.'
  }
  if (notice === 'workwise-cleared') {
    return 'WorkWise cannot be combined with other products, so it was deselected.'
  }
  if (workwiseSelected) {
    return 'WorkWise is deployed on its own: an app server plus a shared or dedicated database and gateway, set up in the next steps. It cannot be combined with other products.'
  }
  return null
}
